// Device — from GET /api/devices (Traccar-joined with device_org_mapping).
export interface Device {
  id: number;                  // Traccar integer id
  uniqueId: string;            // IMEI
  name: string;
  status: 'online' | 'offline' | 'unknown';
  disabled: boolean;
  phone?: string | null;
  model?: string | null;
  contact?: string | null;
  category?: string | null;
  groupId?: number | null;
  lastUpdate?: string | null;  // ISO timestamp
  attributes?: Record<string, unknown>;
  customFields?: Record<string, unknown>;  // from device_org_mapping.custom_fields
}

// Position — from GET /api/positions and /api/positions/:deviceId.
export interface Position {
  id: number;
  deviceId: number;
  fixTime: string;             // ISO
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;               // KNOTS (Traccar-native) — convert before comparing to km/h thresholds
  course: number;
  address?: string | null;
  attributes?: {
    ignition?: boolean;
    fuel?: number;
    totalDistance?: number;    // meters
    hours?: number;            // milliseconds
    [key: string]: unknown;
  };
}

// Rule — from GET /api/rules.
export interface Rule {
  id: string;
  orgId: string;
  name: string;
  enabled: boolean;
  devices: number[];           // empty [] means "all org devices"
  conditions: RuleCondition[];
  actions: RuleActions;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type RuleCondition =
  | { type: 'deviceOverspeed'; threshold: number }
  | { type: 'enterGeofence'; geofenceId: number }
  | { type: 'exitGeofence'; geofenceId: number }
  | { type: 'speedInGeofence'; geofenceId: number; threshold: number }
  | { type: 'maintenanceDueSoon' }
  | { type: 'maintenanceOverdue' }
  | { type: 'enterHighRiskArea' }
  // Other condition types exist (20+ total) — extend as the mobile UI needs them.
  | { type: string; [key: string]: unknown };

export interface RuleActions {
  email?: { recipients: string[] };
  sms?: { recipients: string[] };
  telegram?: { chatIds: string[] };
  whatsapp?: { numbers: string[] };
  push?: { tokens: string[] };
}

// User / Session — from GET /api/auth/session (see server/src/auth/auth.routes.ts).
export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  isSultan: boolean;
  administrator?: boolean;     // derived shim on frontend (see CLAUDE.md)
}

export interface OrgContext {
  id: string;
  name: string;
  role: 'admin' | 'manager' | 'viewer';
  settings?: Record<string, unknown>;
}

export interface Session {
  user: User;
  org?: OrgContext | null;
  organizations?: { id: string; name: string; role: string }[];
  token?: string;
  // Command App Profiles (mobile) — the server resolves the effective
  // per-org app config and rides it here. Loosely typed as `Record<string,
  // unknown>` (not the real `AppProfileConfig` shape) to avoid a
  // types -> formatters package dependency; mobile narrows through
  // `captureAppConfigFromSession`'s parameter type instead.
  appConfig?: { command?: Record<string, unknown> | null } | null;
  // Instance roles (migration 079) — set only for platform staff (Instance
  // Admin / Support); `null` for ordinary customer-org users.
  instance?: { role: 'admin' | 'support'; capabilities: string[] } | null;
}

// Dispatch route planner — Phase 1.
// Server source: server/src/dispatch/routes.routes.ts
export type RouteStatus = 'draft' | 'active' | 'completed' | 'cancelled';
export type StopStatus =
  | 'pending'
  | 'en_route'
  | 'arrived'
  | 'completed'
  | 'skipped'
  | 'failed';
export type OptimizationCriterion = 'time' | 'time_traffic' | 'distance';
export type StopKind = 'job' | 'poi' | 'manual';

export interface DispatchRoute {
  id: string;
  organizationId: string;
  name: string;
  vehicleId: number | null;
  driverId: number | null;
  status: RouteStatus;
  optimizationCriterion: OptimizationCriterion | null;
  roundTrip: boolean;
  plannedFor: string | null;          // ISO
  optimizedAt: string | null;         // ISO
  totalDistanceMeters: number | null;
  totalDurationSeconds: number | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Hydrated by GET /:id; absent on the list endpoint where stopCount is used.
  stops?: RouteStop[];
  stopCount?: number;
}

export interface RouteStop {
  id: string;
  routeId: string;
  sequenceNumber: number;
  // Discriminator derived server-side from which FK is set. `manual` covers
  // both the original manual-address case and the orphaned-after-FK-delete
  // case where job_id/poi_id became NULL.
  kind: StopKind;
  jobId: string | null;
  poiId: string | null;
  manualAddress: string | null;
  latitude: number;
  longitude: number;
  displayName: string;
  contactName: string | null;
  contactPhone: string | null;
  notes: string | null;
  expectedInspectionTemplateId: string | null;
  requiresProofOfVisit: boolean;
  // Phase-3 fields — populated by the Worker mobile app, null in Phase 1.
  status: StopStatus;
  completedAt: string | null;
  completedBy: string | null;
  inspectionSubmissionId: string | null;
  proofPhotoUrls: string[] | null;
  proofSignatureUrl: string | null;
  completionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export type { DevicePhoto } from './devicePhoto.js';
export { STAFF_ROLES } from './staff.js';
export type { StaffRole } from './staff.js';
export { RULE_TARGETS } from './rules.js';
export type { RuleTarget } from './rules.js';
export type { StaffPosition } from './staffPosition.js';
export type { WeatherCurrent, WeatherAqi, ForecastSlot, WeatherPoint } from './weather.js';

// API error shape thrown by @trackizy/api-client.
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | null,
    message: string,
    public details?: unknown,
    // The full parsed JSON error body, when there was one. `details` is a
    // server-opted-in field for a specific error's structured payload (e.g.
    // a shift-overlap conflict); `body` is unconditional so a caller can read
    // OTHER top-level fields a response carries alongside error/code/message
    // (e.g. intelligence's 429 `remainingQueries`/`limit`) without every
    // route having to thread its extra data through `details` too.
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
